<template>
    <div class="offer-item" :class="type">
        <flex-container>
            <div class="offer-item__block full-width">
                <flex-container class="offer-item__head offer-item__name" :style="{ flexWrap: 'wrap' }" align="center">
                    <div class="inline mr-10">
                        <button
                            v-if="'is_selected' in offer"
                            class="offer-item__radio"
                            :class="{ 'offer-item__radio_active': offer.is_selected }"
                            @click="$emit('select')"
                        >
                            <div class="circle"></div>
                        </button>
                        <button
                            v-if="'is_checked' in offer"
                            class="offer-item__check"
                            :class="{ 'offer-item__check_active': offer.is_checked }"
                            @click="$emit('check')"
                        >
                            <CheckIcon />
                        </button>
                        <span @click="$emit('check')" class="cursor-pointer" v-if="'is_checked' in offer">
                            {{ offer.title }}
                        </span>
                        <span @click="$emit('select')" class="cursor-pointer" v-if="'is_selected' in offer">
                            {{ offer.title }}
                        </span>
                    </div>

                    <div v-if="offer.discount && offer.cost_info" class="offer-item__price offer-item__price_old">
                        {{ offer.cost_info.price.amount_formatted }}
                    </div>
                    <div class="offer-item__price" v-else>
                        <template v-if="offer.is_free && showFreeTitle">
                            {{ t("payments.public-offer.free") }}
                        </template>
                        <template v-else-if="!offer.is_free">
                            <price-with-converted
                                class="amount-formatted"
                                :price="offer.price_info.price.amount_formatted"
                                :show-converted="!!getVolatile(offer.price_info)"
                                :price-converted="getVolatile(offer.price_info)"
                                :default-currency="offer.price_info.price.currency.code"
                                :absolute-converted-container="false"
                            />
                        </template>
                    </div>

                    <!-- Discount offer container -->
                    <div class="full-width">
                        <flex-container
                            direction="column"
                            align="center"
                            v-if="offer.discount"
                            class="offer-item__discount mt-10"
                        >
                            <p v-if="discountEnded" class="ended-discount">{{ t("payments.discount_offer_ended") }}</p>
                            <template v-else>
                                <flex-container class="flex-wrap" align="center">
                                    <p class="title">{{ t("payments.discount_offer") }}</p>
                                    <price-with-converted
                                        class="discount-price"
                                        :price="offer.price_info.price.amount_formatted"
                                        :show-converted="!!getVolatile(offer.price_info)"
                                        :price-converted="getVolatile(offer.price_info)"
                                        :default-currency="offer.price_info.price.currency.code"
                                        :absolute-converted-container="false"
                                        :tooltipPosition="$screenWidth > 520 ? 'bottom-center' : 'bottom-left'"
                                    />
                                </flex-container>

                                <p class="date" v-if="offer.discount.end_at_utc && offer.discount.end_type !== 3">
                                    {{ t("payments.price_is_valid_until") }}
                                    {{ formatDateWithTimeNoSeconds(offer.discount.end_at_utc) }}
                                </p>
                            </template>
                        </flex-container>
                    </div>
                </flex-container>
                <div v-if="offer.validity_end_at" class="offer-item__date">
                    {{ t("payments.purchase_available_until") }}
                    {{ formatDateWithTimeNoSeconds(offer.validity_end_at) }}
                </div>

                <!-- Discount offer warning message -->
                <usual-alert
                    v-if="showSalesLimitMsg"
                    :styles="{
                        background: '#FFFDF5',
                        border: '1px solid #F9F1D0',
                        color: '#52565C',
                        padding: '10px',
                        fontSize: '12px'
                    }"
                    class="mt-10 mb-5 discount-info-width"
                >
                    <p class="alert">
                        <span class="alert-text mr-2">{{ t("marketing.attention") }}!</span>
                        <!-- Discount limit -->
                        <template v-if="offer.discount">
                            <!-- Offer limit count -->
                            <span v-if="discountSalesLimit">
                                {{ t("payments.limit_offer_left") }}
                                <span class="accent-text">{{ discountSalesLimit }}</span>
                                {{ t("payments.limit_q-ty") }}
                            </span>
                            <!-- End of sales limit -->
                            <span v-if="!discountSalesLimit">
                                {{ t("payments.limit_ended") }}
                            </span>
                            <!-- Offer with discount end date -->
                            <span v-else-if="offer.discount.end_at_utc">
                                {{ t("payments.limit_offer_ends") }}
                                <span class="bold-text main-accent-text"
                                    >{{ formatDateDdMmYYYY(offer.discount.end_at_utc) }}!</span
                                >
                            </span>
                        </template>

                        <!-- Common offer limit -->
                        <template v-else>
                            <!-- Offer limit count -->
                            <span v-if="offer.has_limit_of_sales">
                                {{ t("payments.limit_offer_left") }}
                                <span class="accent-text">{{ offer.rest_limit_of_sales_count }}</span>
                                {{ t("payments.limit_q-ty") }}
                            </span>
                            <!-- End of sales limit -->
                            <span v-else>{{ t("payments.limit_ended") }}</span>
                        </template>
                    </p>
                </usual-alert>
                <!-- Offer description -->
                <div class="py-10 px-15" v-if="hasDescription() || hasShort()">
                    <flex-container v-if="!hasShort()" :direction="isHidden ? 'row' : 'column'" class="inline-flex">
                        <div
                            ref="description"
                            :style="descriptionStyle"
                            class="offer-item__description relative"
                            :class="{ 'with-dots': showDescriptionMoreButtons && isHidden }"
                        >
                            <ExtendedRenderHtml
                                v-if="!didUpdate"
                                :html="createText(offer.description || offer.short_description)"
                            />
                        </div>

                        <template v-if="showDescriptionMoreButtons">
                            <div v-if="isHidden" @click="isHidden = false" class="offer-item__collapse">
                                {{ t("payments.public-offer._more") }}
                            </div>
                            <div v-if="!isHidden" @click="isHidden = true" class="offer-item__collapse">
                                {{ t("payments.public-offer._hide") }}
                            </div>
                        </template>
                    </flex-container>
                    <div v-else>
                        <div
                            style="max-height: max-content; white-space: initial"
                            ref="description"
                            class="offer-item__description relative"
                        >
                            <ExtendedRenderHtml
                                v-if="!didUpdate"
                                :html="createText(isHidden ? offer.short_description : offer.description)"
                            />
                        </div>
                        <template v-if="showDescriptionMoreButtons">
                            <div v-if="isHidden" @click="isHidden = false" class="offer-item__collapse">
                                {{ t("payments.public-offer._more") }}
                            </div>
                            <div v-if="!isHidden" @click="isHidden = true" class="offer-item__collapse">
                                {{ t("payments.public-offer._hide") }}
                            </div>
                        </template>
                    </div>
                </div>
            </div>
        </flex-container>
    </div>
</template>

<script>
import FlexContainer from "@components/Containers/FlexContainer"
import ExtendedRenderHtml from "@components/ExtendedRender/ExtendedRenderHtml.vue"
import CheckIcon from "@icons/CheckIcon"
import PriceWithConverted from "@components/PriceWithConverted"
import formatDate from "@mixins/formatDate"
import UsualAlert from "@components/Alerts/UsualAlert"

export default {
    components: { ExtendedRenderHtml, UsualAlert, PriceWithConverted, CheckIcon, FlexContainer },
    props: {
        offer: {
            type: Object,
            required: true
        },
        type: String,
        showFreeTitle: {
            type: Boolean,
            default: false
        }
    },
    name: "PublicOfferItem",
    data() {
        return {
            isHidden: true,
            nowTimestamp: null,
            untilTimestamp: null,
            timerInterval: 0,
            discountEnded: false,
            didUpdate: false
        }
    },
    mixins: [formatDate],
    created() {
        if (this.offer.is_checked) {
            this.isHidden = false
        }
        if (this.offer?.discount?.end_type !== 3) {
            this.addDiscountTimer()
        }

        if (this.hasImage() && !this.hasShort()) {
            this.isHidden = false
        }
    },
    methods: {
        createText(description) {
            return `<div class="text-view">${description}</div>`
        },
        hasShort() {
            return (
                this.offer.short_description &&
                this.offer.description &&
                this.offer.short_description !== '<p style="text-align: left"></p>'
            )
        },
        hasDescription() {
            return this.offer.description && this.offer.description !== '<p style="text-align: left"></p>'
        },
        getVolatile(price_info) {
            return !price_info.price_converted.to_show ? "" : `${price_info.price_converted.amount_formatted}`
        },
        discountTimer() {
            const dateNow = new Date()
            this.nowTimestamp = this.toTimestamp(dateNow)
            this.untilTimestamp = this.toTimestamp(this.offer.discount.end_at_utc)

            this.timerInterval = setInterval(() => {
                this.nowTimestamp += 1
                // Stop timer with wrong conditions
                if (this.nowTimestamp >= this.untilTimestamp) {
                    clearInterval(this.timerInterval)
                    this.stopTimer()
                }
            }, 1000)
        },
        stopTimer() {
            this.discountEnded = true
            this.$emit("update")
        },
        addDiscountTimer() {
            if (this.offer.discount && this.offer.discount.end_at_utc && this.type === "upsell") {
                this.discountTimer()
            }
        },
        hasImage() {
            return (
                this.offer.description &&
                (this.offer.description.includes("<img") || this.offer.description.includes("<iframe"))
            )
        }
    },
    computed: {
        descriptionStyle() {
            if (!this.isHidden) {
                return {
                    whiteSpace: "initial",
                    maxHeight: "max-content"
                }
            }
            return {}
        },
        showDescriptionMoreButtons() {
            return this.hasDescription()
        },
        showSalesLimitMsg() {
            return this.offer.discount?.has_limit_of_sales || this.offer.has_limit_of_sales
        },
        // commonSalesLimit() {
        //     return this.offer.limit_of_sales
        // },
        discountSalesLimit() {
            if (
                this.offer.rest_limit_of_sales_count > 0 &&
                this.offer.discount?.rest_limit_of_sales_count > this.offer.rest_limit_of_sales_count
            ) {
                return this.offer.rest_limit_of_sales_count
            }

            return this.offer.discount?.rest_limit_of_sales_count || this.offer.rest_limit_of_sales_count || null
            // if (this.offer.discount?.rest_limit_of_sales_count) {
            //     return this.offer.discount.rest_limit_of_sales_count
            // } else if (this.offer.discount && !this.offer.discount.has_limit_of_sales && this.offer.limit_of_sales) {
            //     return this.commonSalesLimit
            // } else {
            //     return null
            // }
        }
    },
    watch: {
        isHidden() {
            this.didUpdate = true

            this.$nextTick(() => {
                this.didUpdate = false
            })
        }
    }
}
</script>

<style lang="sass">
@import 'resources/sass/vars'

.accent-text
    color: $accent-color5

.discount-price
    font-size: 13px
    line-height: 130%
    font-weight: 600
    @extend .accent-text
</style>

<style scoped lang="sass">
@import 'resources/sass/vars'

.danger
    color: $danger-color

.main-accent-text
    color: $text-color

.bold-text
    font-weight: 600

.alert-text
    @extend .danger
    @extend .bold-text

.discount-info-width
    width: 100%
    max-width: 366px

.ended-discount
    text-align: center
    font-size: 16px
    @extend .alert-text

.offer-item
    padding: 8px 0
    &.crossSell
        background-color: #fbefce
        border: 2px dashed #ff8b00
        border-radius: 10px
        padding-left: 8px
        padding-right: 8px
        &:not(&:last-child)
            margin-bottom: 20px
        .offer-item__name, .offer-item__price
            color: #fff
            &::v-deep
                .amount-formatted
                    b
                        color: #fff
        .offer-item__head
            padding: 10px 15px
            background-color: #ffa02d
    &__head
        border-radius: 10px
    &__collapse
        font-size: 13px
        font-weight: 400
        line-height: 22px
        text-decoration: underline
        text-underline-offset: 3px
        color: #3451BE
        cursor: pointer
    &__discount
        @extend .discount-info-width
        padding: 10px
        border-radius: 8px
        background: $bgc11
        .title
            font-size: 14px
            font-weight: 600
            color: $text-color
        .date
            font-size: 13px
            color: $text-accent-color
    &__description
        color: #52565C
        font-size: 13px
        font-weight: 400
        line-height: 22px
        //max-width: 435px
        overflow: hidden
        //white-space: nowrap
        text-overflow: ellipsis
        max-height: 22px
        margin-right: 10px
        &::v-deep
            iframe
                height: auto
                max-width: 100%
                aspect-ratio: 16 / 9
            h1
                font-size: 16px
                line-height: 1.5
            p
                margin-bottom: 5px
                text-overflow: ellipsis
                overflow: hidden
            ul
                padding-left: 20px
                list-style: inherit
            li
                margin-bottom: 2px
        //@media (max-width: 960px)
        //    white-space: inherit
        &.with-dots
            padding-right: 8px
            word-break: break-all
            &::after
                content: "..."
                position: absolute
                top: 0
                right: 0
    &__date
        margin-top: 5px
        font-size: 11px
        font-weight: 400
        line-height: 14px
        color: #98A2B0
    &__name, &__price
        font-size: 16px
        font-family: Inter, sans-serif
        font-weight: 400
        color: #151515
        line-height: 180%
    &__price
        font-weight: 600
        &_old
            text-decoration: line-through
            color: #B5C1D2
            margin-right: 5px
        &::v-deep
            .amount-formatted
                line-height: 24px
            .tooltip-wrap
                right: auto
                @media (max-width: 520px)
                    right: -50%
    &__check
        width: 20px
        margin-right: 5px
        transform: translateY(3px)
        min-width: 20px
        height: 20px
        border-radius: 4px
        border: 1px solid #DDDDDD
        display: inline-flex
        align-items: center
        justify-content: center
        cursor: pointer
        background-color: #fff
        svg
            fill: #fff
        &_active
            border-color: #15A06E
            background-color: #15A06E
            svg
                fill: #fff
    &__radio
        width: 16px
        min-width: 16px
        margin-right: 5px
        height: 16px
        border-radius: 50%
        border: 1px solid #DDDDDD
        display: inline-flex
        align-items: center
        justify-content: center
        cursor: pointer
        .circle
            width: 10px
            height: 10px
            border-radius: 50%
            background-color:  #DDDDDD
        &_active
            border-color: #15A06E
            .circle
                background-color: #15A06E
</style>
